/**
 * Tabs — WAI-ARIA tabs with roving tabindex (§14.3 keyboard navigation).
 * Direction-aware: ArrowRight/ArrowLeft follow the reading direction, so
 * arrows feel natural in both AR (RTL) and EN (LTR) (§14.2). Home/End jump
 * to the first/last enabled tab. Activation follows focus (standard for
 * in-page tabs with cheap panels).
 *
 *   <Tabs value={v} onValueChange={setV} label={t("orders.tabs")} tabs={…}>
 *     <TabPanel value="all">…</TabPanel>
 *   </Tabs>
 */

import {
  createContext,
  useContext,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { cx } from "../utils/cx";

export interface TabItem {
  readonly value: string;
  readonly label: ReactNode;
  readonly disabled?: boolean;
  /** Optional count/status adornment rendered after the label. */
  readonly badge?: ReactNode;
}

interface TabsContextValue {
  readonly value: string;
  readonly idBase: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

export interface TabsProps {
  readonly tabs: ReadonlyArray<TabItem>;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  /** Accessible name for the tablist (i18n slot, §14.3). */
  readonly label: string;
  readonly children?: ReactNode;
  readonly className?: string;
}

export function Tabs({ tabs, value, onValueChange, label, children, className }: TabsProps) {
  const idBase = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const enabled = tabs.filter((t) => t.disabled !== true);

  const move = (from: string, delta: -1 | 1 | "first" | "last") => {
    if (enabled.length === 0) return;
    let next: TabItem | undefined;
    if (delta === "first") next = enabled[0];
    else if (delta === "last") next = enabled[enabled.length - 1];
    else {
      const index = enabled.findIndex((t) => t.value === from);
      next = enabled[(index + delta + enabled.length) % enabled.length];
    }
    if (next) {
      onValueChange(next.value);
      document.getElementById(`${idBase}-tab-${next.value}`)?.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tabValue: string) => {
    // §14.2 — arrows follow the visual reading direction of the tablist.
    // The dir attribute (set per locale on <html> or a subtree) is the
    // authority; computed style is the fallback for CSS-only direction.
    const el = listRef.current;
    const dirAttr = el?.closest("[dir]")?.getAttribute("dir");
    const rtl = dirAttr ? dirAttr === "rtl" : el != null && getComputedStyle(el).direction === "rtl";
    const forward: -1 | 1 = rtl ? -1 : 1;
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        move(tabValue, forward);
        break;
      case "ArrowLeft":
        event.preventDefault();
        move(tabValue, forward === 1 ? -1 : 1);
        break;
      case "Home":
        event.preventDefault();
        move(tabValue, "first");
        break;
      case "End":
        event.preventDefault();
        move(tabValue, "last");
        break;
      default:
    }
  };

  return (
    <TabsContext.Provider value={{ value, idBase }}>
      <div className={cx("ui-tabs", className)}>
        <div className="ui-tabs-list" role="tablist" aria-label={label} ref={listRef}>
          {tabs.map((tab) => {
            const selected = tab.value === value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                id={`${idBase}-tab-${tab.value}`}
                aria-selected={selected}
                aria-controls={`${idBase}-panel-${tab.value}`}
                disabled={tab.disabled}
                tabIndex={selected ? 0 : -1}
                className={cx("ui-tab", selected && "ui-tab--selected")}
                onClick={() => {
                  onValueChange(tab.value);
                }}
                onKeyDown={(event) => {
                  handleKeyDown(event, tab.value);
                }}
              >
                {tab.label}
                {tab.badge != null ? <span className="ui-tab-badge">{tab.badge}</span> : null}
              </button>
            );
          })}
        </div>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export interface TabPanelProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly className?: string;
}

/** Renders only while active — panels stay cheap, activation follows focus. */
export function TabPanel({ value, children, className }: TabPanelProps) {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("TabPanel must be used inside <Tabs>");
  if (ctx.value !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${ctx.idBase}-panel-${value}`}
      aria-labelledby={`${ctx.idBase}-tab-${value}`}
      tabIndex={0}
      className={cx("ui-tab-panel", className)}
    >
      {children}
    </div>
  );
}
